import { Card, CardActions, CardContent, Grid } from "@mui/material";
import { StyledComponentProps } from "@mui/styles";
import { FeedClassKey } from "assets/jss/common-styles";
import ButtonGetAnswer from "components/Buttons/ButtonGetAnswer";
import ButtonIgnoreAction from "components/Buttons/ButtonIgnoreAction";
import { RequestWrapIndicator } from "components/CallServer/RequestWrapIndicator";
import MuiContainer from "components/MuiExtras/MuiContainer";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import { ApprovalStatus, FeedType, PublicationActionType, PublicationVisility } from "models/ApiEnums";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { GetPublicationResultMessage } from "views/Publication/Components/GetPublicationResultMessage";
import CreateAnswer from "views/Publication/CreateAnswer";
import { ActionOwnBody, ActionRequesterBody, ActionSuggestionBody } from "views/Timeline/Components/CardComp/ActionBody";
import ActionsDefaultDropDown from "views/Timeline/Components/CardComp/ActionsDefaultDropDown";
import ActionsDefaultRight from "views/Timeline/Components/CardComp/ActionsDefaultRight";
import ActionsPublisher from "views/Timeline/Components/CardComp/ActionsPublisher";
import ActionsQuestion from "views/Timeline/Components/CardComp/ActionsQuestion";
import ActionsSocial from "views/Timeline/Components/CardComp/ActionsSocial";
import BodyDefault from "views/Timeline/Components/CardComp/BodyDefault";
import FooterLine from "views/Timeline/Components/CardComp/FooterLine";
import HeaderLine from "views/Timeline/Components/CardComp/HeaderLine";
import IconsLine from "views/Timeline/Components/CardComp/IconsLine";
import TitleLine from "views/Timeline/Components/CardComp/TitleLine";
import { RenderDefaultApprovalAction, RenderDenouncementAction } from "views/Timeline/Components/CardFactory/CommonRenders";
import { CardCommonActions } from "views/Timeline/Components/CardLib/CardCommonActions";
import { QuestionCardSetup } from "views/Timeline/Components/CardLib/CardSetup";
import { ICardProps } from "views/Timeline/Components/CardLib/ICardProps";
import Delete from "views/Timeline/Dialogs/PublicationDelete";
import Edit from "views/Timeline/Dialogs/PublicationEdit";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    mouseOver: boolean;
    showBody: boolean;
    dialog: any;
}

type Props = ICardProps & WithSnackbar & WithTranslation & StyledComponentProps<FeedClassKey>;

/////////////////////////////////////////////////////////////////////////////////////

class QuestionCard extends React.PureComponent<Props, IState> {

    private commonActions: CardCommonActions;

    constructor(props: Props) {
        super(props);
        this.state = {
            dialog: null,
            mouseOver: false,
            showBody: null
        };

        this.commonActions = new CardCommonActions(props.onUpdateCard, props.snackbar.showMessage);
    }

    ///
    private processEdit = () => {
        const { item } = this.props;

        if (this.props.onBeforeEdit) {
            this.setState({ mouseOver: false });
            if (!this.props.onBeforeEdit()) return;
        }

        this.setState({
            mouseOver: false,
            dialog: (
                <Edit
                    data={item}
                    onClose={
                        (update: boolean) => {
                            if (update) {
                                this.props.onReloadPublication();
                            }
                            this.setState({ dialog: null });
                        }
                    }
                />
            )
        });
    };

    ///
    private processDelete = () => {
        const { item } = this.props;

        new Promise<boolean>((resolve) => {
            if (this.props.onBeforeDelete) {
                this.props.onBeforeDelete()
                    .then((result) => resolve(result));
            }
            else
                resolve(false);
        }).then((handled) => {
            if (!handled) {

                this.setState({
                    mouseOver: false,
                    dialog: (
                        <Delete
                            data={item}
                            onRemovePublication={this.props.onRemovePublication}
                            onClose={() => this.setState({ dialog: null })}
                        />)
                });

            }
        });
    };

    ///
    private handleAddAnswer = () => {
        const { item } = this.props;

        this.setState({
            dialog: <CreateAnswer
                questionId={item.publicationId}
                questionTitle={item.publication.title}
                onClose={
                    (result) => {
                        this.setState({ dialog: null }, () => {
                            if (result != null) {
                                const r = GetPublicationResultMessage(result);
                                if (r.message) this.props.snackbar.showMessage(r.message, r.messageOption);

                                if (this.props.onAddCard) this.props.onAddCard(result.publicationId);

                                if (this.props.onReloadPublication) this.props.onReloadPublication();
                            }
                        });
                    }
                }
            />
        });
    };

    ///
    private handleRepassIgnore = (actionId: string) => {
        if (this.props.onIgnoreActionRequest) {

            this.props.onIgnoreActionRequest(actionId)
                .then(() => {
                    this.props.snackbar.showMessage(this.props.t("Common.Feed.Question.QuestionIgnoredSuccess"), "success");
                });
        }
    };

    ///
    private handleTextExpand = (textExpanded: boolean) => {
        if (this.props.item.bestAnswer == null)
            this.commonActions.expandText(this.props.item, textExpanded);
        else
            this.commonActions.expandBestAnswerText(this.props.item, textExpanded);

        this.setState({ showBody: textExpanded });
    };

    ///
    private renderQuestionDefaultActions(env: QuestionCardSetup) {
        const {
            classes,
            feedType,
            item,
            showRelationsButton,
            onSidebarResetRequest,
            onUpdateCard
        } = this.props;


        return (
            <CardActions disableSpacing className={classes.cardActions} >
                <Grid container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center">
                    <Grid item>
                        {
                            (env.fullView || item.bestAnswer == null)
                                ? (
                                    <ActionsQuestion
                                        classes={classes}
                                        data={item}
                                        fullView={env.fullView}
                                        onRequestNewPublication={this.handleAddAnswer}
                                        onUpdateCard={onUpdateCard}
                                    />
                                )
                                : (
                                    <ActionsSocial
                                        data={item}
                                        fullView={env.fullView}
                                        showCommentsButton={true}
                                        onUpdateCard={onUpdateCard}
                                    />
                                )
                        }
                    </Grid>

                    <Grid item>

                        <ActionsDefaultRight
                            classes={classes}
                            data={item}
                            fullView={env.fullView}
                            showAddToFavorites
                            showShareButton
                            showRelationsButton={showRelationsButton && env.fullView}
                            onUpdateCard={onUpdateCard}
                            onSidebarResetRequest={() => onSidebarResetRequest()}
                        />

                        <ActionsDefaultDropDown
                            classes={classes}
                            data={item}
                            feedType={feedType}
                            fullView={env.fullView}
                            onEditPublication={env.editHandle}
                            onRemovePublication={env.deleteHandle}
                            onReloadPublication={env.reloadHandle}
                            onUpdateCard={onUpdateCard}
                            onInfoRequest={this.props.onInfoRequest}
                        />
                    </Grid>
                </Grid>
            </CardActions>
        );
    }

    ///
    private renderRepassAction() {
        const {
            classes,
            item,
        } = this.props;

        return (
            <div>
                <MuiContainer margin="dense">
                    <ActionRequesterBody
                        classes={classes}
                        data={item}
                    />
                </MuiContainer>

                <MuiContainer margin="none" flex={true} className={classes.cardActions}>
                    <ButtonGetAnswer onClick={this.handleAddAnswer} />
                    <ButtonIgnoreAction onClick={() => this.handleRepassIgnore(item.action.actionId)} />
                </MuiContainer>
            </div>
        );
    }

    ///
    private processVisibility = () => {
        this.setState({
            mouseOver: false,
        });

        this.props.onChangePublicationVisibility(
            this.props.item.interaction.visibility == PublicationVisility.Hidden
                ? PublicationVisility.Visible
                : PublicationVisility.Hidden
        );
    };

    ///
    public render() {
        const {
            feedType,
            classes,
            item,
            onApprovalRequest,
            onUpdateCard
        } = this.props;

        const { mouseOver } = this.state;

        const env = new QuestionCardSetup(this.props, this.state.showBody, this.processEdit, this.processDelete, null, this.processVisibility);

        // Resultado
        return (
            <>
                <Card
                    id={item.publicationId}
                    className={classes.card}
                    onMouseEnter={() => this.setState({ mouseOver: true })}
                    onMouseLeave={() => this.setState({ mouseOver: false })
                    }
                >
                    <RequestWrapIndicator enabled={item.isUpdating} type="update">

                        <CardContent className={classes.cardContent}>

                            <Grid container>
                                <Grid item xs>
                                    { /* Area 1 */}
                                    <HeaderLine
                                        classes={classes}
                                        data={item}
                                        feedType={this.props.feedType}
                                        fullView={env.fullView}
                                        inApprovalMode={env.inApprovalMode}
                                        mouseOver={mouseOver}
                                        textExpanded={env.textExpanded}
                                        topMessageMode={env.topMessageMode}
                                        onUpdateCard={(item) => onUpdateCard(item)}
                                    />

                                    { /* Area 3 */}
                                    <TitleLine
                                        classes={classes}
                                        data={item}
                                        feedType={this.props.feedType}
                                        fullView={env.fullView}
                                        onTitleClick={env.titleHandle}
                                    />
                                    {
                                        (feedType == FeedType.TaskForYourApproval || feedType == FeedType.TaskDenouncement || feedType == FeedType.TaskForYourAction || feedType == FeedType.TaskGeneralView) && item.action.actionType == PublicationActionType.ApproveSuggestion
                                            ? <ActionSuggestionBody classes={classes} data={item} />
                                            : feedType == FeedType.TaskRequestedByYou
                                                ? <ActionOwnBody classes={classes} data={item} />
                                                : null
                                    }
                                </Grid>
                                <Grid item>
                                    { /* Area 2 */}
                                    <IconsLine
                                        data={item}
                                        feedType={this.props.feedType}
                                        mouseOver={mouseOver}
                                        textExpanded={env.textExpanded}
                                        classes={classes}
                                        onEditPublication={env.editHandle}
                                        onHidePublication={env.visibilityHandle}
                                        onRemovePublication={env.deleteHandle}
                                        onViewLater={item.permissions.canViewLater ? this.commonActions.viewLater : null}
                                        onTextExpandedChange={
                                            (textExpanded) => {
                                                this.setState({ showBody: textExpanded });
                                                return feedType == FeedType.MyPublication
                                                    ? this.commonActions.expandBestAnswerText(item, textExpanded)
                                                    : env.fullView || item.bestAnswer == null
                                                        ? this.commonActions.expandText(item, textExpanded)
                                                        : this.commonActions.expandBestAnswerText(item, textExpanded);
                                            }
                                        }
                                        onCustomAction={this.props.onCustomAction}
                                    />
                                </Grid>
                            </Grid>

                            {
                                /* Area 4, 5 e 6 */
                                env.showBodyRegion &&
                                <BodyDefault
                                    classes={classes}
                                    data={item}
                                    feedType={feedType}
                                    fullView={env.fullView}
                                    alwaysExpanded={env.alwaysExpanded}
                                    showExpandButtonIfContentEmpty={env.isDenouncement}
                                    onTextExpandedChange={
                                        (textExpanded) => item.bestAnswer == null
                                            ? this.commonActions.expandText(item, textExpanded)
                                            : this.commonActions.expandBestAnswerText(item, textExpanded)}
                                />
                            }

                            {
                                /* Area 7 */
                                env.hasAccess &&
                                <>
                                    {

                                        feedType == FeedType.MyPublication && item.publication.approvalStatus != ApprovalStatus.Approved
                                            ? (
                                                <ActionsPublisher
                                                    data={item}
                                                    onEditPublication={env.editHandle}
                                                    onRemovePublication={env.deleteHandle}
                                                    removeInDropDown={item.publication.approvalStatus == ApprovalStatus.Pending || item.publication.approvalStatus == ApprovalStatus.PendingCognitiveAnalysis}
                                                />
                                            )
                                            : feedType == FeedType.QuestionToYou && env.questionRepassedToMe
                                                ? this.renderRepassAction()
                                                : (feedType != FeedType.TaskRequestedByYou && !env.questionRepassedToMe
                                                    && item.publication.approvalStatus === ApprovalStatus.Approved
                                                    && (env.fullView || env.textExpanded == null || env.textExpanded === true || (!env.isDenouncement && item.bestAnswer == null)))
                                                    ? this.renderQuestionDefaultActions(env)
                                                    : null
                                    }

                                </>
                            }

                            {
                                /* Area 8 */
                                env.hasAccess &&
                                <>
                                    {

                                        !env.inApprovalMode
                                            ? null
                                            : env.questionRepassedToMe
                                                ? this.renderRepassAction()
                                                : env.isDenouncement
                                                    ? <RenderDenouncementAction
                                                        classes={classes}
                                                        item={item}
                                                        hasBody={env.hasBody}
                                                        showBody={env.showBody}
                                                        onApprovalRequest={onApprovalRequest}
                                                        onExpandText={this.handleTextExpand}
                                                        onUpdateCard={onUpdateCard}
                                                    />
                                                    : <RenderDefaultApprovalAction
                                                        classes={classes}
                                                        item={item}
                                                        hasBody={env.hasBody}
                                                        showBody={env.showBody}
                                                        onApprovalRequest={onApprovalRequest}
                                                        onExpandText={this.handleTextExpand}
                                                        onUpdateCard={onUpdateCard}
                                                    />
                                    }
                                </>
                            }


                        </CardContent>

                        <FooterLine
                            classes={classes}
                            data={item}
                            fullView={env.fullView}
                        />

                    </RequestWrapIndicator>
                </Card >

                {this.state.dialog}
            </>
        );
    }
}

export default withSnackbar(withTranslation()(QuestionCard));